import React, { useMemo, useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import { MapPin, Users, Flame } from 'lucide-react';

// Fix Leaflet default marker icons for Webpack/CRA
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const DEFAULT_CENTER = [-2.5489, 118.0149]; // Indonesia center
const DEFAULT_ZOOM = 5;

// Performance tiers (based on voucher_sold count)
const TIER_PRODUKTIF = 5;   // >= 5 sold → hijau
const TIER_AKTIF = 1;       // 1-4 sold → oranye
// 0 sold → abu-abu (belum aktif jual)

function getTier(voucherSold) {
  const s = Number(voucherSold || 0);
  if (s >= TIER_PRODUKTIF) return { key: 'produktif', color: '#16a34a', label: 'Produktif' };
  if (s >= TIER_AKTIF) return { key: 'aktif', color: '#f59e0b', label: 'Aktif' };
  return { key: 'stagnant', color: '#94a3b8', label: 'Belum Jual' };
}

// Build a colored circular divIcon per tier
function makeAgentIcon(color) {
  return L.divIcon({
    className: 'agent-marker',
    html: `<div style="background:${color};width:28px;height:28px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:14px;">A</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 14);
      return;
    }
    const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [points, map]);
  return null;
}

// Heatmap layer — weight by voucher_sold; agen tanpa penjualan tetap kelihatan tipis
function HeatLayer({ points, enabled }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || !points || points.length === 0) return undefined;
    // Normalize weights: sold=0 -> 0.15, sold>=10 -> 1.0
    const maxSold = Math.max(1, ...points.map(p => p.voucher_sold || 0));
    const heatPoints = points.map(p => {
      const w = 0.15 + 0.85 * Math.min(1, (p.voucher_sold || 0) / Math.max(1, maxSold));
      return [p.lat, p.lng, w];
    });
    const layer = L.heatLayer(heatPoints, {
      radius: 45,
      blur: 30,
      maxZoom: 15,
      gradient: { 0.0: '#a855f7', 0.4: '#f59e0b', 0.7: '#22c55e', 1.0: '#16a34a' },
    });
    map.addLayer(layer);
    return () => { map.removeLayer(layer); };
  }, [points, enabled, map]);
  return null;
}

const fmtIDR = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

/**
 * AgentsMiniMap
 * Props:
 *   agents: array of { id, name, latitude, longitude, branch_name, phone, whatsapp,
 *                       voucher_total, voucher_sold, commission_total }
 *   height: px (default 340)
 */
export default function AgentsMiniMap({ agents = [], height = 360 }) {
  const [heatOn, setHeatOn] = useState(false);

  const points = useMemo(
    () => (agents || [])
      .filter(a => a.latitude != null && a.longitude != null && !isNaN(parseFloat(a.latitude)) && !isNaN(parseFloat(a.longitude)))
      .map(a => ({
        id: a.id,
        name: a.name,
        lat: parseFloat(a.latitude),
        lng: parseFloat(a.longitude),
        branch_name: a.branch_name,
        phone: a.phone,
        whatsapp: a.whatsapp,
        voucher_total: a.voucher_total || 0,
        voucher_sold: a.voucher_sold || 0,
        commission_total: a.commission_total || 0,
      })),
    [agents],
  );

  const totalAgents = (agents || []).length;
  const mapped = points.length;
  const missing = totalAgents - mapped;

  // Tier stats
  const tierStats = useMemo(() => {
    const s = { produktif: 0, aktif: 0, stagnant: 0 };
    points.forEach(p => { s[getTier(p.voucher_sold).key]++; });
    return s;
  }, [points]);

  if (totalAgents === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 p-6 text-center text-sm text-slate-500 dark:text-slate-400">
        <MapPin className="w-8 h-8 mx-auto mb-2 opacity-40" />
        Belum ada agen voucher terdaftar.
      </div>
    );
  }

  if (mapped === 0) {
    return (
      <div className="rounded-lg border border-dashed border-amber-300 dark:border-amber-600/50 p-6 text-center text-sm text-amber-700 dark:text-amber-300 bg-amber-50/50 dark:bg-amber-900/20">
        <MapPin className="w-8 h-8 mx-auto mb-2 opacity-60" />
        {totalAgents} agen belum memiliki titik koordinat.
        <div className="text-xs mt-1 opacity-80">Klik Edit pada agen, lalu tekan "Ambil Lokasi" untuk mengatur titik koordinat.</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Info & Controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600 dark:text-slate-300">
        <div className="flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5 text-purple-600" />
          <span><strong>{mapped}</strong> dari <strong>{totalAgents}</strong> agen memiliki titik koordinat</span>
          {missing > 0 && (
            <span className="ml-2 text-amber-600 dark:text-amber-400">({missing} belum di-set)</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setHeatOn(v => !v)}
          data-testid="toggle-heatmap-btn"
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
            heatOn
              ? 'bg-orange-500 text-white border-orange-500 hover:bg-orange-600'
              : 'bg-white text-orange-600 border-orange-300 hover:bg-orange-50 dark:bg-slate-800 dark:text-orange-300 dark:border-orange-800/60'
          }`}
        >
          <Flame className="w-3 h-3" /> Heatmap Coverage {heatOn ? 'ON' : 'OFF'}
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-[11px]">
        <span className="text-slate-500 dark:text-slate-400 font-medium">Legenda:</span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-emerald-600 border-2 border-white shadow-sm"></span>
          <span className="text-slate-700 dark:text-slate-200">Produktif (≥{TIER_PRODUKTIF} terjual)</span>
          <span className="text-emerald-700 dark:text-emerald-300 font-semibold">{tierStats.produktif}</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-amber-500 border-2 border-white shadow-sm"></span>
          <span className="text-slate-700 dark:text-slate-200">Aktif (1-{TIER_PRODUKTIF - 1} terjual)</span>
          <span className="text-amber-700 dark:text-amber-300 font-semibold">{tierStats.aktif}</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-slate-400 border-2 border-white shadow-sm"></span>
          <span className="text-slate-700 dark:text-slate-200">Belum Jual</span>
          <span className="text-slate-500 dark:text-slate-400 font-semibold">{tierStats.stagnant}</span>
        </span>
      </div>

      {/* Map */}
      <div style={{ height, width: '100%' }} className="rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
        <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={points} />
          <HeatLayer points={points} enabled={heatOn} />
          {points.map(p => {
            const tier = getTier(p.voucher_sold);
            return (
              <Marker key={p.id} position={[p.lat, p.lng]} icon={makeAgentIcon(tier.color)}>
                <Popup>
                  <div className="text-sm space-y-1 min-w-[190px]">
                    <div className="font-bold text-purple-700 flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" /> {p.name}
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px]">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ background: tier.color }}
                      ></span>
                      <span className="font-medium" style={{ color: tier.color }}>{tier.label}</span>
                    </div>
                    {p.branch_name && (
                      <div className="text-[11px] text-slate-500">{p.branch_name}</div>
                    )}
                    {(p.phone || p.whatsapp) && (
                      <div className="text-[11px] text-slate-600">{p.phone || p.whatsapp}</div>
                    )}
                    <div className="pt-1 border-t border-slate-100 space-y-0.5 text-[11px]">
                      <div>Total voucher: <strong>{p.voucher_total}</strong></div>
                      <div>Terjual: <strong className="text-emerald-600">{p.voucher_sold}</strong></div>
                      <div>Komisi: <strong className="text-amber-600">{fmtIDR(p.commission_total)}</strong></div>
                    </div>
                    <div className="pt-1 text-[10px] text-slate-400">
                      {p.lat.toFixed(5)}, {p.lng.toFixed(5)}
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}
